import { useRef, useEffect, useState, useCallback } from 'react'

import { MESSAGE_STATUS } from '../constants'

/**
 * Custom hook to track when the top and bottom of a component become visible
 * Logs to console only once per component instance
 *
 * @param {Object} params - The parameters object
 * @param {string} params.status - The status of the message from MESSAGE_STATUS constants
 * @param {string} params.queryId - The id of the query
 * @param {string} params.responseId - The id of the response
 * @param {Function} params.trackResponseView - Function to track response view events
 * @returns {Object} Object containing refs for top and bottom elements
 */
export const useVisibilityTracking = ({ status, queryId = null, responseId = null, trackResponseView = () => {} }) => {
  const topElementRef = useRef(null)
  const bottomElementRef = useRef(null)

  // State to track if we've already logged for this instance
  const [hasLoggedTop, setHasLoggedTop] = useState(false)
  const [hasLoggedBottom, setHasLoggedBottom] = useState(false)

  const getAnalyticsResultCategory = useCallback(() => {
    switch (status) {
      case MESSAGE_STATUS.SUCCESS:
        return 'ai_response_success'
      case MESSAGE_STATUS.ERROR:
        return 'ai_response_error'
      case MESSAGE_STATUS.CANCELLED:
        return 'ai_response_cancelled'
      default:
        return 'null'
    }
  }, [status])

  const handleTrackResponseView = useCallback((isTop) => {
    const resultCategory = getAnalyticsResultCategory()
    trackResponseView(isTop, queryId, resultCategory, responseId)
  }, [trackResponseView, queryId, responseId, getAnalyticsResultCategory])

  useEffect(() => {
    const topObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasLoggedTop) {
            handleTrackResponseView(true)
            setHasLoggedTop(true)
          }
        })
      }
    )

    const bottomObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasLoggedBottom) {
            handleTrackResponseView(false)
            setHasLoggedBottom(true)
          }
        })
      }
    )

    // Start observing when refs are available
    if (topElementRef.current) {
      topObserver.observe(topElementRef.current)
    }
    if (bottomElementRef.current) {
      bottomObserver.observe(bottomElementRef.current)
    }

    // Cleanup observers
    return () => {
      topObserver.disconnect()
      bottomObserver.disconnect()
    }
  }, [status, queryId, responseId, hasLoggedTop, hasLoggedBottom, handleTrackResponseView])

  return {
    topElementRef,
    bottomElementRef
  }
}
